import { useEffect, useState } from 'react';

import { env } from '@/env';
import { qgammonAuthHeaders } from '@/lib/qgammon/auth';
import { useGetMeQuery } from '@/services/qgammonApi';
import { useAppSelector } from '@/store/hooks';
import type { Player } from '@qlashgeminiz/qgammon-rules';

/**
 * UNA PARTITA CHE ESISTE E DI CUI IL GIOCATORE NON SA NIENTE.
 *
 * ═══ IL BUCO, E PERCHE' IL DEEP LINK LO RENDE NORMALE ═══
 * Quando qualcuno accetta un invito, il gameserver crea il match e avvisa il
 * creatore SOLO sui socket vivi:
 *   `for (const sid of index.socketIdsFor(creator.id)) io.to(sid).emit(...)`
 * (`invites/routes.ts`). E `useInvite` chiude il socket allo smontaggio della
 * sheet. Con un codice letto a voce l'accettazione arriva in secondi e il
 * creatore e' li' a guardare; **col deep link arriva dopo il login dell'ospite,
 * cioe' minuti dopo e a sheet chiusa** — e la partita parte con i suoi orologi.
 * 🔑 Il deep link non introduce questo buco: lo sposta da raro a NORMALE.
 *
 * ═══ PERCHE' LA CURA STA NEL CLIENT, E NON E' UNA PEZZA ═══
 * Ho misurato prima di costruire: lo stato durevole ESISTE gia' e ha gia' una
 * porta autenticata. `GET /matches` sul gameserver ritorna le partite VIVE del
 * chiamante dal registro. Quel che si perdeva era solo la NOTIFICA, che e'
 * fire-and-forget per costruzione. ⇒ Interrogare lo stato durevole non nasconde
 * un difetto di server: e' la risposta giusta a una notifica che, per come e'
 * fatta, puo' sempre non arrivare.
 * ⛔ La pezza sbagliata sarebbe stata tenere il socket aperto piu' a lungo:
 * curerebbe la sheet chiusa e non l'app chiusa, cioe' proprio il caso del deep
 * link.
 *
 * ⚠️ E NON si usa `GET /matches/mine` di qgammon-api: [M 06/09] quella rotta
 * filtra `status IN (completed, abandoned, voided)` — e' lo STORICO, e una
 * partita appena creata non c'e'. Due rotte quasi omonime su due servizi
 * diversi, e quella col nome piu' invitante e' quella sbagliata.
 */

export interface ResumableMatch {
  matchId: string;
  /** Il MIO colore: senza questo il tabellone parte dal lato sbagliato. */
  perspective: Player;
  opponentName: string;
}

interface LiveMatchRow {
  matchId: string;
  white: { userId: string; displayName: string };
  black: { userId: string; displayName: string };
  ended: boolean;
}

export function useResumableMatch(): ResumableMatch | null {
  // `/auth/me` di qgammon-api da' l'id INTERNO (`publicUser.id`), lo stesso
  // spazio di id di `white.userId`/`black.userId` del gameserver: i due servizi
  // condividono la stessa tabella Prisma. ⛔ NON e' il `sub` di Logto —
  // `requireAuth` lo dichiara: «`sub` aliases the internal User.id».
  const { data: me } = useGetMeQuery();
  const active = useAppSelector((s) => s.activeMatch.current);
  const [found, setFound] = useState<ResumableMatch | null>(null);

  const myId = me?.user?.id ?? null;
  const activeMatchId = active?.matchId ?? null;

  useEffect(() => {
    if (!myId) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`${env.qgammonWsUrl}/matches`, {
          headers: await qgammonAuthHeaders(),
        });
        if (!res.ok || cancelled) return;
        const body = (await res.json()) as { matches?: LiveMatchRow[] };
        if (cancelled) return;
        // ⚠️ Il filtro su `ended` non e' pignoleria: il registro tiene una
        // partita finita per un giro prima che `finalizeMatch` la cancelli, e
        // senza questo si inviterebbe a «riprendere» una partita conclusa.
        const live = (body.matches ?? []).filter((m) => !m.ended);
        // Quella che sto gia' giocando non e' una scoperta.
        const candidate = live.find((m) => m.matchId !== activeMatchId);
        if (!candidate) {
          setFound(null);
          return;
        }
        const iAmWhite = candidate.white.userId === myId;
        setFound({
          matchId: candidate.matchId,
          perspective: iAmWhite ? 'W' : 'B',
          opponentName: iAmWhite
            ? candidate.black.displayName
            : candidate.white.displayName,
        });
      } catch {
        // Il gameserver muto non e' una notizia da dare: senza risposta non so
        // se una partita c'e', e affermare che non c'e' sarebbe un vuoto
        // affermato su una domanda mai andata a buon fine.
        if (!cancelled) setFound(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [myId, activeMatchId]);

  return found;
}
